import React, {
  useState,
  useEffect,
  useMemo,
  useContext,
  useCallback,
} from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Button,
  Form,
  Row,
  Col,
  Alert,
  Table,
  Spinner,
  Modal,
  Container,
} from "react-bootstrap";
import {
  FiAlertTriangle,
  FiAward,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiCpu,
  FiEdit3,
  FiFileText,
  FiGrid,
  FiLayers,
  FiPlus,
  FiRefreshCw,
  FiRotateCcw,
  FiSave,
  FiTrash2,
  FiZap,
  FiX,
} from "react-icons/fi";
import AuthContext from "../context/AuthContext";
import AiProviderFields from "./AiProviderFields";
import "./ScheduleManagement.css";

const API_URL = process.env.REACT_APP_API_URL;

const roundTypeMap = {
  2: "Vòng 1/16",
  3: "Vòng 1/8",
  4: "Tứ Kết",
  5: "Bán Kết",
  6: "Chung Kết",
};

const ScheduleManagement = () => {
  const [seasons, setSeasons] = useState([]);
  const [teamLevels, setTeamLevels] = useState([]);
  const [tables, setTables] = useState([]);
  const [teams, setTeams] = useState([]);

  const [selectedSeason, setSelectedSeason] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("");
  const [currentRound, setCurrentRound] = useState(1);

  const [groupStageSchedule, setGroupStageSchedule] = useState([]);
  const [knockoutSchedule, setKnockoutSchedule] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const [notification, setNotification] = useState({ type: "", msg: "" });
  const [pointBatches, setPointBatches] = useState([]);

  // AI State
  const [prompt, setPrompt] = useState(
    "Tạo lịch thi đấu vòng tròn 1 lượt cho tất cả các đội trong cùng một bảng.",
  );
  const [aiDraft, setAiDraft] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiConfig, setAiConfig] = useState({
    base_url: "",
    api_key: "",
    model: "",
    json_mode: 1,
  });

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [modalMode, setModalMode] = useState("group");
  const [currentMatch, setCurrentMatch] = useState({});
  const [modalTeamList, setModalTeamList] = useState([]);
  const [activeTab, setActiveTab] = useState("group-stage");

  const { user } = useContext(AuthContext);

  const [groupStageRounds, setGroupStageRounds] = useState([]);

  // Simplified fetch for details of a list of matches
  const fetchMatchDetails = async (matches) => {
    return Promise.all(
      matches.map(async (match) => {
        try {
          const detailRes = await axios.get(`${API_URL}/schedules/${match.id}`);
          const subMatchesRes = await axios.get(`${API_URL}/details/match-details/${match.id}`);
          const subMatches = subMatchesRes.data || [];
          const confirmed_sub_matches = subMatches.filter(sm => sm.guest_status === 1).length;
          const total_sub_matches = subMatches.length;
          return { ...match, ...detailRes.data, confirmed_sub_matches, total_sub_matches };
        } catch (e) {
          console.error(`Could not fetch details for match ${match.id}`, e);
          return match;
        }
      })
    );
  };


  // Fetch initial static data (seasons, levels)
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [seasonsRes, levelsRes] = await Promise.all([
          axios.get(`${API_URL}/seasons`),
          axios.get(`${API_URL}/team-levels`),
        ]);
        const seasonsData = seasonsRes.data;
        const levelsData = levelsRes.data;
        setSeasons(seasonsData);
        setTeamLevels(levelsData);

        if (seasonsData.length > 0) {
          setSelectedSeason(seasonsData[0].id);
        }
        if (levelsData.length > 0) {
          const serieA = levelsData.find(l => l.name.toLowerCase() === "serie a");
          if (serieA) {
            setSelectedLevel(serieA.id);
          } else {
            setSelectedLevel(levelsData[0].id);
          }
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
        setError("Không thể tải dữ liệu khởi tạo.");
      }
    };
    fetchInitialData();
  }, []);


  // Fetch non-schedule data when season/level changes
  useEffect(() => {
    const fetchDeps = async () => {
      if (!selectedSeason || !selectedLevel) return;
      setLoading(true);
      try {
        const [tablesRes, teamsRes] = await Promise.all([
          axios.get(`${API_URL}/details/tables?season_id=${selectedSeason}&level_team_id=${selectedLevel}`),
          axios.get(`${API_URL}/teams/teams-by-level?level_team_id=${selectedLevel}`),
        ]);
        setTables(tablesRes.data);
        setTeams(teamsRes.data);
      } catch (error) {
        setError("Không thể tải dữ liệu bảng và đội.");
      } finally {
        setLoading(false);
      }
    };
    fetchDeps();
  }, [selectedSeason, selectedLevel]);


  // Fetch rounds when season/level changes
  useEffect(() => {
    const fetchRounds = async () => {
      if (!selectedSeason || !selectedLevel) return;
      try {
        const res = await axios.get(`${API_URL}/schedules/rounds?season_id=${selectedSeason}&level_team_id=${selectedLevel}`);
        setGroupStageRounds(res.data);
        if (res.data.length > 0) {
          setCurrentRound(res.data[0]);
        } else {
          setCurrentRound(null);
          setGroupStageSchedule([]); // Clear schedule if no rounds
        }
      } catch (error) {
        console.error("Error fetching rounds:", error);
        setError("Không thể tải danh sách vòng đấu.");
      }
    };
    fetchRounds();
  }, [selectedSeason, selectedLevel, needsRefresh]);


  // Fetch group stage schedules when tab is active and filters change
  useEffect(() => {
    const fetchGroupSchedules = async () => {
      if (activeTab !== 'group-stage' || !selectedSeason || !selectedLevel) return;

      setLoading(true);
      setError("");
      try {
        let groupMatches = [];
        if (currentRound) {
          const groupRes = await axios.get(`${API_URL}/schedules?season_id=${selectedSeason}&level_team_id=${selectedLevel}&round_id=${currentRound}&round_type=1`);
          groupMatches = await fetchMatchDetails(groupRes.data);
        }
        setGroupStageSchedule(groupMatches);
      } catch (error) {
        setError("Không thể tải lịch thi đấu vòng bảng.");
        console.error("Error fetching group schedules:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchGroupSchedules();
  }, [activeTab, selectedSeason, selectedLevel, currentRound, needsRefresh]);

  // Fetch knockout schedules when tab is active and filters change
  useEffect(() => {
    const fetchKnockoutSchedules = async () => {
      if (activeTab !== 'knockout-stage' || !selectedSeason || !selectedLevel) return;

      setLoading(true);
      setError("");
      try {
        const knockoutRes = await axios.get(`${API_URL}/schedules?season_id=${selectedSeason}&level_team_id=${selectedLevel}&round_type_gt=1`);
        const detailedKnockout = await fetchMatchDetails(knockoutRes.data);
        setKnockoutSchedule(detailedKnockout);
      } catch (error) {
        setError("Không thể tải lịch thi đấu loại trực tiếp.");
        console.error("Error fetching knockout schedules:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchKnockoutSchedules();
  }, [activeTab, selectedSeason, selectedLevel, needsRefresh]);

  useEffect(() => {
    const fetchPointBatches = async () => {
      if (user?.role !== "admin") return;
      try {
        const res = await axios.get(`${API_URL}/points/batches`);
        setPointBatches(res.data || []);
      } catch (error) {
        console.error("Error fetching point batches:", error);
      }
    };
    fetchPointBatches();
  }, [user?.role, needsRefresh]);

  const handleClose = () => setShowModal(false);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setCurrentMatch((prev) => ({ ...prev, [name]: value }));
    if (name === "table_id") {
      const selectedTable = tables.find((t) => t.id === parseInt(value));
      setModalTeamList(selectedTable ? selectedTable.teams : []);
      setCurrentMatch((prev) => ({ ...prev, home: "", guest: "" }));
    }
  };

  const handleShowAdd = (mode) => {
    setModalMode(mode);
    setIsEditing(false);
    setCurrentMatch(
      mode === "group"
        ? {
          round_type: 1,
          table_id: "",
          round_id: "",
          play_date: "",
          home: "",
          guest: "",
        }
        : { round_type: 4, round_id: "", play_date: "", home: "", guest: "", knockout_format: "single", division: "", division_name: "" },
    );
    if (mode === "knockout") setModalTeamList(teams);
    setShowModal(true);
  };

  const handleShowEdit = (match) => {
    setModalMode(match.round_type === 1 ? "group" : "knockout");
    setIsEditing(true);
    setCurrentMatch(match);
    if (match.round_type === 1) {
      const selectedTable = tables.find((t) => t.id === match.table_id);
      setModalTeamList(selectedTable ? selectedTable.teams : []);
    } else {
      setModalTeamList(teams);
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (currentMatch.home === currentMatch.guest) {
      alert("Đội nhà và đội khách không được trùng nhau.");
      return;
    }
    const payload = {
      ...currentMatch,
      season_id: selectedSeason,
      level_team_id: selectedLevel,
    };
    try {
      if (isEditing) {
        await axios.put(`${API_URL}/schedules/${currentMatch.id}`, payload);
      } else {
        await axios.post(`${API_URL}/schedules`, payload);
      }
      setNeedsRefresh((p) => !p);
      handleClose();
    } catch (error) {
      console.error("Error saving match:", error);
    }
  };

  const handleDeleteMatch = async (id) => {
    if (window.confirm("Bạn có chắc chắn muốn xóa trận đấu này?")) {
      await axios.delete(`${API_URL}/schedules/${id}`);
      setNeedsRefresh((p) => !p);
    }
  };

  const handleGenerateWithAI = async () => {
    if (!prompt) return;
    setAiLoading(true);
    setAiDraft(null);
    setError("");
    try {
      const res = await axios.post(`${API_URL}/schedules/generate-with-ai`, {
        prompt,
        season_id: selectedSeason,
        level_team_id: selectedLevel,
        ai_config: aiConfig,
      });
      const draftData = res.data.matches || res.data;
      if (Array.isArray(draftData)) {
        setAiDraft(draftData);
      } else {
        throw new Error("AI response is not a valid array.");
      }
    } catch (error) {
      setError(
        error.response?.data?.message ||
          error.response?.data ||
          "Lỗi khi tạo lịch với AI.",
      );
      console.error(error);
    } finally {
      setAiLoading(false);
    }
  };

  const handleSaveAIDraft = async () => {
    if (!aiDraft || aiDraft.length === 0) return;
    if (!window.confirm("Bạn có chắc chắn muốn LƯU lịch thi đấu này?")) return;
    try {
      await axios.post(`${API_URL}/schedules/bulk-create`, {
        season_id: selectedSeason,
        level_team_id: selectedLevel,
        matches: aiDraft,
      });
      setAiDraft(null);
      setNeedsRefresh((p) => !p);
      alert("Lưu lịch thành công!");
    } catch (error) {
      setError(error.response?.data || "Lỗi khi lưu lịch AI.");
    }
  };

  const handleUpdatePoints = async () => {
    setLoading(true);
    setNotification({ type: "", msg: "" });
    try {
      const res = await axios.post(`${API_URL}/points/calculate-round`, {
        season_id: selectedSeason,
        level_team_id: selectedLevel,
        round_id: currentRound,
      });
      setNotification({ type: "success", msg: res.data });
      setNeedsRefresh((p) => !p);
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Lỗi cập nhật điểm.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateKnockoutPoints = async (roundType) => {
    setLoading(true);
    setNotification({ type: "", msg: "" });
    try {
      const res = await axios.post(`${API_URL}/points/calculate-knockout`, {
        season_id: selectedSeason,
        level_team_id: selectedLevel,
        round_type: roundType,
      });
      setNotification({ type: "success", msg: res.data });
      setNeedsRefresh((p) => !p);
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Lỗi cập nhật điểm vòng loại.",
      });
    } finally {
      setLoading(false);
    }
  };

  const getRollbackBatch = useCallback(
    ({ roundType, roundId = null }) => {
      return pointBatches.find((batch) => {
        const sameSeason = String(batch.season_id) === String(selectedSeason);
        const sameLevel = String(batch.level_team_id) === String(selectedLevel);
        const sameRoundType = String(batch.round_type || "") === String(roundType || "");
        const sameRound =
          roundId === null || roundId === undefined
            ? true
            : String(batch.round_id || "") === String(roundId);
        return (
          sameSeason &&
          sameLevel &&
          sameRoundType &&
          sameRound &&
          batch.mode === "commit" &&
          batch.status === "committed"
        );
      });
    },
    [pointBatches, selectedSeason, selectedLevel],
  );

  const handleRollbackBatch = async ({ roundType, roundId = null, label }) => {
    const batch = getRollbackBatch({ roundType, roundId });
    if (!batch) {
      setNotification({
        type: "warning",
        msg: `${label} chưa có batch tính điểm để rollback.`,
      });
      return;
    }
    const confirmMessage = `Rollback ${label} về trạng thái trước batch #${batch.id}? Nếu vòng này nằm trước các vòng đã tính điểm, cần tính lại các vòng sau theo thứ tự.`;
    if (!window.confirm(confirmMessage)) return;

    setLoading(true);
    setNotification({ type: "", msg: "" });
    try {
      const res = await axios.post(`${API_URL}/points/batches/${batch.id}/rollback`);
      setNotification({
        type: "success",
        msg: `Đã rollback ${label}. Rollback batch #${res.data.rollbackBatchId}.`,
      });
      setNeedsRefresh((p) => !p);
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data?.message || err.response?.data || "Lỗi rollback điểm.",
      });
    } finally {
      setLoading(false);
    }
  };

  const getTeamNameById = (id) => {
    const team = teams.find((t) => t.id === id);
    return team ? team.name : "Không rõ";
  };

  const groupedSchedule = useMemo(() => {
    if (!groupStageSchedule) return {};
    return groupStageSchedule.reduce((acc, match) => {
      const tableName = match.table_name;
      const roundId = match.round_id;
      if (!acc[tableName]) {
        acc[tableName] = {};
      }
      if (!acc[tableName][roundId]) {
        acc[tableName][roundId] = [];
      }
      acc[tableName][roundId].push(match);
      return acc;
    }, {});
  }, [groupStageSchedule]);



  const groupedKnockoutSchedule = useMemo(() => {
    if (!knockoutSchedule) return {};
    return knockoutSchedule.reduce((acc, match) => {
      // Single format: group by round_type. Two-division: group by division + round_type.
      const key = match.knockout_format === "two_division" && match.division
        ? `${match.division}::${match.round_type}`
        : `${match.round_type}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(match);
      return acc;
    }, {});
  }, [knockoutSchedule]);

  const selectedSeasonName = useMemo(() => {
    const season = seasons.find((s) => s.id === selectedSeason);
    return season ? season.name : "";
  }, [seasons, selectedSeason]);

  const selectedLevelName = useMemo(() => {
    const level = teamLevels.find((l) => l.id === selectedLevel);
    return level ? level.name : "";
  }, [teamLevels, selectedLevel]);

  // Check if current round has matches eligible for point update
  const canUpdateGroupPoints = useMemo(() => {
    if (!groupStageSchedule || !currentRound) return false;
    const currentRoundMatches = groupStageSchedule.filter(
      (match) => match.round_id === currentRound
    );
    return currentRoundMatches.some(
      (match) => match.guest_has_confirmed && match.status === 0
    );
  }, [groupStageSchedule, currentRound]);

  // Check if knockout round has matches eligible for point update
  const canUpdateKnockoutPoints = useCallback((roundType) => {
    if (!knockoutSchedule) return false;
    const roundMatches = knockoutSchedule.filter(
      (match) => match.round_type === parseInt(roundType)
    );
    return roundMatches.some(
      (match) => match.guest_has_confirmed && match.status === 0
    );
  }, [knockoutSchedule]);

  const currentRoundTables = useMemo(() => {
    if (!currentRound) return [];
    return Object.entries(groupedSchedule).filter(
      ([, rounds]) => rounds[currentRound] && rounds[currentRound].length > 0
    );
  }, [groupedSchedule, currentRound]);

  const currentGroupRollbackBatch = useMemo(() => {
    if (!currentRound) return null;
    return getRollbackBatch({ roundType: 1, roundId: currentRound });
  }, [getRollbackBatch, currentRound]);

  const knockoutRoundEntries = useMemo(() => {
    return Object.entries(groupedKnockoutSchedule).sort(([a], [b]) => {
      const aKey = a.includes("::") ? a.split("::")[1] : a;
      const bKey = b.includes("::") ? b.split("::")[1] : b;
      return Number(aKey) - Number(bKey);
    });
  }, [groupedKnockoutSchedule]);

  const scheduleStats = useMemo(() => {
    const groupMatches = groupStageSchedule || [];
    const knockoutMatches = knockoutSchedule || [];
    const allMatches = [...groupMatches, ...knockoutMatches];
    const pendingConfirmations = allMatches.filter(
      (match) =>
        match.status === 0 &&
        (match.home_result !== null || match.guest_result !== null) &&
        !match.guest_has_confirmed
    ).length;
    const readyForPoints = allMatches.filter(
      (match) => match.guest_has_confirmed && match.status === 0
    ).length;

    return {
      group: groupMatches.length,
      knockout: knockoutMatches.length,
      pendingConfirmations,
      readyForPoints,
    };
  }, [groupStageSchedule, knockoutSchedule]);

  const formatDate = (date) => {
    if (!date) return "Chưa đặt ngày";
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return date;
    return parsed.toLocaleDateString("vi-VN");
  };

  const getInitials = (name) => {
    if (!name) return "BB";
    return name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase();
  };

  const getMatchStatus = (match) => {
    const hasScore = match.home_result !== null || match.guest_result !== null;
    if (match.status === 1) {
      return { label: "Đã tính điểm", tone: "success", icon: FiCheckCircle };
    }
    if (match.guest_has_confirmed) {
      return { label: "Đã xác nhận", tone: "success", icon: FiCheckCircle };
    }
    if (hasScore) {
      return { label: "Chờ xác nhận", tone: "warning", icon: FiClock };
    }
    return { label: "Chưa đấu", tone: "info", icon: FiCalendar };
  };

  const renderMatchRow = (match, context = "group") => {
    const isAdmin = user && user.role === "admin";
    const isHomeManager =
      user &&
      user.role === "manager" &&
      String(user.teamId) === String(match.home);
    const isGuestManager =
      user &&
      user.role === "manager" &&
      String(user.teamId) === String(match.guest);
    const canOpenMatch = isAdmin || isHomeManager || isGuestManager;
    const actionLabel = isGuestManager && !isAdmin && !isHomeManager
      ? "Xác nhận / video"
      : "Nhập KQ";
    const actionTitle = isGuestManager && !isAdmin && !isHomeManager
      ? "Xác nhận kết quả hoặc bổ sung video"
      : "Nhập kết quả trận đấu";
    const status = getMatchStatus(match);
    const StatusIcon = status.icon;

    return (
      <article className="schedule-match-row" key={match.id}>
        <div className="match-date-block">
          <span className="match-date">{formatDate(match.play_date)}</span>
          <span className="match-code">#{match.id}</span>
          {match.division === "nhom1" && (
            <span className="match-code" style={{ color: "#eab308" }}>Vô Địch</span>
          )}
          {match.division === "nhom2" && (
            <span className="match-code" style={{ color: "#22d3ee" }}>Hy Vọng</span>
          )}
        </div>

        <div className="match-team match-team-home">
          <div className="team-mark">{getInitials(match.home_name)}</div>
          <div>
            <div className="team-name">{match.home_name || "Đội nhà"}</div>
            <div className="team-meta">{match.home_point ?? 0} điểm</div>
          </div>
        </div>

        <div className="match-score-block">
          <div className="match-score">
            <span>{match.home_result ?? "-"}</span>
            <span className="score-separator">:</span>
            <span>{match.guest_result ?? "-"}</span>
          </div>
          {context === "group" && match.total_sub_matches > 0 && (
            <div className="sub-match-note">
              Xác nhận {match.confirmed_sub_matches} / {match.total_sub_matches}
            </div>
          )}
        </div>

        <div className="match-team">
          <div className="team-mark team-mark-away">
            {getInitials(match.guest_name)}
          </div>
          <div>
            <div className="team-name">{match.guest_name || "Đội khách"}</div>
            <div className="team-meta">{match.guest_point ?? 0} điểm</div>
          </div>
        </div>

        <div className="match-status-cell">
          <span className={`schedule-status schedule-status-${status.tone}`}>
            <StatusIcon />
            {status.label}
          </span>
        </div>

        <div className="match-actions">
          {canOpenMatch && (
            <Button
              as={Link}
              to={`/schedules/${match.id}`}
              className="schedule-action-btn schedule-action-primary"
              size="sm"
              title={actionTitle}
            >
              <FiFileText />
              <span>{actionLabel}</span>
            </Button>
          )}
          {isAdmin && (
            <>
              <Button
                className="schedule-icon-btn"
                size="sm"
                title="Chỉnh sửa trận đấu"
                onClick={() => handleShowEdit(match)}
              >
                <FiEdit3 />
              </Button>
              <Button
                className="schedule-icon-btn schedule-icon-danger"
                size="sm"
                title="Xóa trận đấu"
                onClick={() => handleDeleteMatch(match.id)}
              >
                <FiTrash2 />
              </Button>
            </>
          )}
        </div>
      </article>
    );
  };

  return (
    <Container fluid className="schedule-admin-page py-4">
      <section className="schedule-hero">
        <div>
          <div className="schedule-kicker">Admin / Lịch thi đấu</div>
          <h2 className="schedule-title">Quản lý lịch thi đấu</h2>
          <p className="schedule-subtitle">
            Điều phối lịch vòng bảng, vòng loại, nhập kết quả, xác nhận và tính điểm.
          </p>
          <div className="schedule-context">
            <span>
              <FiCalendar />
              {selectedSeasonName || "Chưa chọn mùa giải"}
            </span>
            <span>
              <FiAward />
              {selectedLevelName || "Chưa chọn hạng đấu"}
            </span>
          </div>
        </div>

        {user?.role === "admin" && (
          <div className="schedule-hero-actions">
            <Button
              className="schedule-secondary-btn"
              onClick={() => handleShowAdd("group")}
              disabled={!selectedSeason || !selectedLevel}
            >
              <FiPlus />
              Trận vòng bảng
            </Button>
            <Button
              className="schedule-primary-btn"
              onClick={() => handleShowAdd("knockout")}
              disabled={!selectedSeason || !selectedLevel}
            >
              <FiPlus />
              Trận loại trực tiếp
            </Button>
          </div>
        )}
      </section>

      {error && (
        <Alert
          variant="danger"
          className="schedule-alert"
          dismissible
          onClose={() => setError("")}
        >
          <FiAlertTriangle />
          {error}
        </Alert>
      )}

      <section className="schedule-filter-panel">
        <div className="filter-field">
          <label>Mùa giải</label>
          <Form.Select
            value={selectedSeason}
            onChange={(e) => setSelectedSeason(e.target.value)}
            className="schedule-select"
          >
            <option value="">Chọn mùa giải</option>
            {seasons.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Form.Select>
        </div>

        <div className="filter-field filter-field-wide">
          <label>Hạng đấu</label>
          <div className="level-selector">
            {teamLevels.map((level) => (
              <button
                key={level.id}
                type="button"
                className={`level-pill ${
                  selectedLevel === level.id ? "level-pill-active" : ""
                }`}
                onClick={() => setSelectedLevel(level.id)}
                disabled={loading}
              >
                {level.name}
              </button>
            ))}
          </div>
        </div>

        <div className="filter-summary">
          <span className="summary-label">Dữ liệu đang xem</span>
          <strong>
            {selectedSeasonName && selectedLevelName
              ? `${selectedSeasonName} / ${selectedLevelName}`
              : "Chưa đủ bộ lọc"}
          </strong>
        </div>
      </section>

      <section className="schedule-stat-grid">
        <article className="schedule-stat-card">
          <div className="stat-icon">
            <FiGrid />
          </div>
          <div>
            <span>Trận vòng bảng</span>
            <strong>{scheduleStats.group}</strong>
          </div>
        </article>
        <article className="schedule-stat-card">
          <div className="stat-icon stat-icon-violet">
            <FiAward />
          </div>
          <div>
            <span>Trận loại trực tiếp</span>
            <strong>{scheduleStats.knockout}</strong>
          </div>
        </article>
        <article className="schedule-stat-card">
          <div className="stat-icon stat-icon-amber">
            <FiClock />
          </div>
          <div>
            <span>Chờ xác nhận</span>
            <strong>{scheduleStats.pendingConfirmations}</strong>
          </div>
        </article>
        <article className="schedule-stat-card">
          <div className="stat-icon stat-icon-blue">
            <FiZap />
          </div>
          <div>
            <span>Sẵn sàng tính điểm</span>
            <strong>{scheduleStats.readyForPoints}</strong>
          </div>
        </article>
      </section>

      {selectedSeason && selectedLevel ? (
        <section className="schedule-workspace">
          <div className="schedule-tabs" role="tablist">
            <button
              type="button"
              className={activeTab === "group-stage" ? "active" : ""}
              onClick={() => setActiveTab("group-stage")}
            >
              <FiGrid />
              Vòng bảng
            </button>
            <button
              type="button"
              className={activeTab === "knockout-stage" ? "active" : ""}
              onClick={() => setActiveTab("knockout-stage")}
            >
              <FiAward />
              Vòng loại trực tiếp
            </button>
            {user?.role === "admin" && (
              <button
                type="button"
                className={activeTab === "actions" ? "active" : ""}
                onClick={() => setActiveTab("actions")}
              >
                <FiCpu />
                Quản trị lịch
              </button>
            )}
          </div>

          {activeTab === "group-stage" && (
            <section className="schedule-panel">
              <div className="schedule-panel-header">
                <div>
                  <h3>Vòng bảng</h3>
                  <p>
                    Chọn vòng để xem lịch theo từng bảng đấu, nhập kết quả và cập nhật điểm.
                  </p>
                </div>
                {user?.role === "admin" && currentRound && (
                  <div className="schedule-header-actions">
                    <Button
                      className={
                        canUpdateGroupPoints
                          ? "schedule-success-btn"
                          : "schedule-disabled-btn"
                      }
                      onClick={handleUpdatePoints}
                      disabled={!canUpdateGroupPoints || loading}
                    >
                      {loading ? <Spinner animation="border" size="sm" /> : <FiRefreshCw />}
                      Tính điểm vòng {currentRound}
                    </Button>
                    <Button
                      className={
                        currentGroupRollbackBatch
                          ? "schedule-danger-btn"
                          : "schedule-disabled-btn"
                      }
                      onClick={() =>
                        handleRollbackBatch({
                          roundType: 1,
                          roundId: currentRound,
                          label: `vòng ${currentRound}`,
                        })
                      }
                      disabled={!currentGroupRollbackBatch || loading}
                      title={
                        currentGroupRollbackBatch
                          ? `Rollback batch #${currentGroupRollbackBatch.id}`
                          : "Vòng này chưa có batch để rollback"
                      }
                    >
                      <FiRotateCcw />
                      Rollback vòng {currentRound}
                    </Button>
                  </div>
                )}
              </div>

              {notification.msg && (
                <Alert
                  variant={notification.type}
                  className="schedule-alert"
                  dismissible
                  onClose={() => setNotification({ msg: "" })}
                >
                  {notification.msg}
                </Alert>
              )}

              {user?.role === "admin" && currentRound && !canUpdateGroupPoints && (
                <div className="schedule-note">
                  <FiAlertTriangle />
                  Vòng {currentRound} chưa có trận đấu nào đủ điều kiện cập nhật điểm.
                </div>
              )}

              {loading ? (
                <div className="schedule-loading">
                  <Spinner animation="border" />
                  <span>Đang tải lịch thi đấu...</span>
                </div>
              ) : groupStageRounds.length > 0 ? (
                <>
                  <div className="round-control">
                    <div className="round-strip">
                      {groupStageRounds.map((round) => (
                        <button
                          key={round}
                          type="button"
                          className={round === currentRound ? "active" : ""}
                          onClick={() => setCurrentRound(round)}
                        >
                          Vòng {round}
                        </button>
                      ))}
                    </div>
                    <Form.Select
                      value={currentRound || ""}
                      onChange={(e) => setCurrentRound(parseInt(e.target.value))}
                      className="round-select-mobile"
                    >
                      {groupStageRounds.map((round) => (
                        <option key={round} value={round}>
                          Vòng {round}
                        </option>
                      ))}
                    </Form.Select>
                  </div>

                  {currentRoundTables.length > 0 ? (
                    <div className="schedule-table-stack">
                      {currentRoundTables.map(([tableName, rounds]) => (
                        <section className="schedule-table-card" key={tableName}>
                          <div className="schedule-table-head">
                            <div>
                              <h4>{tableName}</h4>
                              <p>Vòng {currentRound}</p>
                            </div>
                            <span>{rounds[currentRound].length} trận</span>
                          </div>
                          <div className="schedule-match-list">
                            {rounds[currentRound].map((match) =>
                              renderMatchRow(match, "group")
                            )}
                          </div>
                        </section>
                      ))}
                    </div>
                  ) : (
                    <div className="schedule-empty-state">
                      <FiCalendar />
                      <strong>Vòng này chưa có trận đấu.</strong>
                      <span>Admin có thể thêm thủ công hoặc tạo bằng AI ở tab Quản trị lịch.</span>
                    </div>
                  )}
                </>
              ) : (
                <div className="schedule-empty-state">
                  <FiCalendar />
                  <strong>Chưa có lịch thi đấu vòng bảng.</strong>
                  <span>Hãy tạo bảng đấu và thêm lịch cho mùa/hạng đang chọn.</span>
                </div>
              )}
            </section>
          )}

          {activeTab === "knockout-stage" && (
            <section className="schedule-panel">
              <div className="schedule-panel-header">
                <div>
                  <h3>Vòng loại trực tiếp</h3>
                  <p>Theo dõi từng nhánh đấu, cập nhật điểm theo từng vòng loại.</p>
                </div>
              </div>

              {loading ? (
                <div className="schedule-loading">
                  <Spinner animation="border" />
                  <span>Đang tải lịch loại trực tiếp...</span>
                </div>
              ) : knockoutRoundEntries.length > 0 ? (
                <div className="knockout-stack">
                  {knockoutRoundEntries.map(([groupKey, matches]) => {
                    const roundType = groupKey.includes("::") ? groupKey.split("::")[1] : groupKey;
                    const divisionId = groupKey.includes("::") ? groupKey.split("::")[0] : null;
                    const divisionLabel = divisionId === "nhom1" ? "Nhóm 1: Vô Địch" : divisionId === "nhom2" ? "Nhóm 2: Hy Vọng (không tính vô địch)" : "";
                    const canUpdate = canUpdateKnockoutPoints(roundType);
                    return (
                      <section className="schedule-table-card" key={groupKey}>
                        <div className="schedule-table-head">
                          <div>
                            <h4>{roundTypeMap[roundType] || `Vòng ${roundType}`}{divisionLabel ? ` · ${divisionLabel}` : ""}</h4>
                            <p>{matches.length} trận loại trực tiếp</p>
                          </div>
                          {user?.role === "admin" && (
                            <div className="schedule-header-actions">
                              <Button
                                className={
                                  canUpdate
                                    ? "schedule-success-btn"
                                    : "schedule-disabled-btn"
                                }
                                onClick={() => handleUpdateKnockoutPoints(roundType)}
                                disabled={!canUpdate || loading}
                              >
                                {loading ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  <FiRefreshCw />
                                )}
                                Tính điểm
                              </Button>
                              <Button
                                className={
                                  getRollbackBatch({ roundType })
                                    ? "schedule-danger-btn"
                                    : "schedule-disabled-btn"
                                }
                                onClick={() =>
                                  handleRollbackBatch({
                                    roundType,
                                    label: roundTypeMap[roundType] || `vòng ${roundType}`,
                                  })
                                }
                                disabled={!getRollbackBatch({ roundType }) || loading}
                                title={
                                  getRollbackBatch({ roundType })
                                    ? `Rollback batch #${getRollbackBatch({ roundType }).id}`
                                    : "Vòng này chưa có batch để rollback"
                                }
                              >
                                <FiRotateCcw />
                                Rollback
                              </Button>
                            </div>
                          )}
                        </div>
                        {user?.role === "admin" && !canUpdate && (
                          <div className="schedule-note schedule-note-inside">
                            <FiAlertTriangle />
                            Chưa có trận đủ điều kiện cập nhật điểm ở vòng này.
                          </div>
                        )}
                        <div className="schedule-match-list">
                          {matches.map((match) => renderMatchRow(match, "knockout"))}
                        </div>
                      </section>
                    );
                  })}
                </div>
              ) : (
                <div className="schedule-empty-state">
                  <FiAward />
                  <strong>Chưa có lịch vòng loại trực tiếp.</strong>
                  <span>Thêm trận loại trực tiếp sau khi vòng bảng đã chốt kết quả.</span>
                </div>
              )}
            </section>
          )}

          {activeTab === "actions" && user?.role === "admin" && (
            <section className="schedule-actions-layout">
              <div className="manual-actions">
                <article className="action-card">
                  <div className="action-icon">
                    <FiGrid />
                  </div>
                  <h3>Thêm trận vòng bảng</h3>
                  <p>Chọn bảng, vòng, ngày thi đấu, đội nhà và đội khách.</p>
                  <Button
                    className="schedule-primary-btn"
                    onClick={() => handleShowAdd("group")}
                  >
                    <FiPlus />
                    Thêm trận vòng bảng
                  </Button>
                </article>

                <article className="action-card">
                  <div className="action-icon action-icon-violet">
                    <FiAward />
                  </div>
                  <h3>Thêm trận loại trực tiếp</h3>
                  <p>Tạo trận knock-out theo vòng 1/16, 1/8, tứ kết, bán kết hoặc chung kết.</p>
                  <Button
                    className="schedule-primary-btn"
                    onClick={() => handleShowAdd("knockout")}
                  >
                    <FiPlus />
                    Thêm trận loại trực tiếp
                  </Button>
                </article>
              </div>

              <article className="ai-panel">
                <div className="ai-panel-header">
                  <div>
                    <div className="action-icon action-icon-blue">
                      <FiCpu />
                    </div>
                    <h3>Tạo lịch bằng AI</h3>
                    <p>Mô tả luật xếp lịch, hệ thống sẽ tạo bản nháp để xem lại trước khi lưu.</p>
                  </div>
                  <Button
                    className="schedule-primary-btn"
                    onClick={handleGenerateWithAI}
                    disabled={aiLoading || !prompt}
                  >
                    {aiLoading ? <Spinner animation="border" size="sm" /> : <FiZap />}
                    Tạo lịch
                  </Button>
                </div>

                <AiProviderFields
                  value={aiConfig}
                  onChange={setAiConfig}
                  disabled={aiLoading}
                />

                <Form.Group>
                  <Form.Label>Yêu cầu tạo lịch</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Ví dụ: Tạo lịch thi đấu vòng tròn 1 lượt cho tất cả các đội trong cùng một bảng..."
                    className="schedule-textarea"
                  />
                </Form.Group>

                {aiDraft && (
                  <div className="ai-preview">
                    <div className="ai-preview-head">
                      <div>
                        <h4>Bản nháp AI</h4>
                        <p>{aiDraft.length} trận đấu đã được tạo.</p>
                      </div>
                      <div className="ai-preview-actions">
                        <Button
                          className="schedule-success-btn"
                          onClick={handleSaveAIDraft}
                        >
                          <FiSave />
                          Lưu lịch
                        </Button>
                        <Button
                          className="schedule-secondary-btn"
                          onClick={() => setAiDraft(null)}
                        >
                          <FiX />
                          Hủy
                        </Button>
                      </div>
                    </div>

                    <div className="ai-table-wrap">
                      <Table hover responsive className="ai-preview-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Vòng</th>
                            <th>Ngày</th>
                            <th>Bảng</th>
                            <th>Đội nhà</th>
                            <th>Đội khách</th>
                          </tr>
                        </thead>
                        <tbody>
                          {aiDraft.map((match, index) => (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td>Vòng {match.round}</td>
                              <td>{formatDate(match.play_date)}</td>
                              <td>
                                {tables.find((t) => t.id === match.table_id)?.name ||
                                  "Chưa rõ"}
                              </td>
                              <td>{getTeamNameById(match.home_team_id)}</td>
                              <td>{getTeamNameById(match.away_team_id)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                )}
              </article>
            </section>
          )}
        </section>
      ) : (
        <section className="schedule-empty-state schedule-empty-large">
          <FiLayers />
          <strong>Chọn mùa giải và hạng đấu để bắt đầu.</strong>
          <span>Trang lịch sẽ tự tải bảng, đội và các vòng đấu tương ứng.</span>
        </section>
      )}

      <Modal
        show={showModal}
        onHide={handleClose}
        size="lg"
        centered
        className="schedule-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditing
              ? "Chỉnh sửa trận đấu"
              : `Thêm trận ${modalMode === "group" ? "vòng bảng" : "loại trực tiếp"}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            {modalMode === "group" ? (
              <Form.Group className="mb-4">
                <Form.Label>Bảng đấu</Form.Label>
                <Form.Select
                  name="table_id"
                  value={currentMatch.table_id}
                  onChange={handleFormChange}
                  required
                  className="schedule-select"
                >
                  <option value="">Chọn bảng</option>
                  {tables.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            ) : (
              <>
                <Form.Group className="mb-4">
                  <Form.Label>Vòng đấu</Form.Label>
                  <Form.Select
                    name="round_type"
                    value={currentMatch.round_type}
                    onChange={handleFormChange}
                    className="schedule-select"
                  >
                    <option value="2">Vòng 1/16</option>
                    <option value="3">Vòng 1/8</option>
                    <option value="4">Tứ Kết</option>
                    <option value="5">Bán Kết</option>
                    <option value="6">Chung Kết</option>
                  </Form.Select>
                </Form.Group>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-4">
                      <Form.Label>Thể thức</Form.Label>
                      <Form.Select
                        name="knockout_format"
                        value={currentMatch.knockout_format || "single"}
                        onChange={handleFormChange}
                        className="schedule-select"
                      >
                        <option value="single">Nhánh đơn (thể thức cũ)</option>
                        <option value="two_division">2 Nhánh: Vô Địch + Hy Vọng</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  {currentMatch.knockout_format === "two_division" && (
                    <Col md={6}>
                      <Form.Group className="mb-4">
                        <Form.Label>Nhánh đấu</Form.Label>
                        <Form.Select
                          name="division"
                          value={currentMatch.division || ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            handleFormChange(e);
                            setCurrentMatch((prev) => ({
                              ...prev,
                              division_name: val === "nhom1" ? "Nhóm 1: Vô Địch" : val === "nhom2" ? "Nhóm 2: Hy Vọng" : "",
                            }));
                          }}
                          className="schedule-select"
                          required
                        >
                          <option value="">Chọn nhánh</option>
                          <option value="nhom1">Nhóm 1: Vô Địch (tính vô địch serie)</option>
                          <option value="nhom2">Nhóm 2: Hy Vọng (không tính vô địch)</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  )}
                </Row>
              </>
            )}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label>Tên vòng / số vòng</Form.Label>
                  <Form.Control
                    type="text"
                    name="round_id"
                    value={currentMatch.round_id}
                    onChange={handleFormChange}
                    required
                    placeholder="Ví dụ: 1, 2, 3..."
                    className="schedule-input"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label>Ngày thi đấu</Form.Label>
                  <Form.Control
                    type="date"
                    name="play_date"
                    value={currentMatch.play_date}
                    onChange={handleFormChange}
                    className="schedule-input"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label>Đội nhà</Form.Label>
                  <Form.Select
                    name="home"
                    value={currentMatch.home}
                    onChange={handleFormChange}
                    required
                    disabled={modalMode === "group" && !currentMatch.table_id}
                    className="schedule-select"
                  >
                    <option value="">Chọn đội</option>
                    {modalTeamList.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label>Đội khách</Form.Label>
                  <Form.Select
                    name="guest"
                    value={currentMatch.guest}
                    onChange={handleFormChange}
                    required
                    disabled={modalMode === "group" && !currentMatch.table_id}
                    className="schedule-select"
                  >
                    <option value="">Chọn đội</option>
                    {modalTeamList.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <div className="schedule-modal-actions">
              <Button className="schedule-secondary-btn" onClick={handleClose}>
                <FiX />
                Hủy
              </Button>
              <Button className="schedule-primary-btn" type="submit">
                <FiSave />
                Lưu trận đấu
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default ScheduleManagement;
